import React from 'react';
import { Block, Label, Select } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';

export type OptionsSource = 'own' | 'clientContacts' | 'companies' | 'products' | 'users';

const LABELS: Record<OptionsSource, string> = {
	own: 'admin.fields.fieldDependency.ownOptions',
	clientContacts: 'admin.fields.fieldDependency.clientContacts',
	companies: 'admin.fields.fieldDependency.companies',
	products: 'admin.fields.fieldDependency.products',
	users: 'admin.fields.fieldDependency.users'
};

/** Which datatype each source saves the field as: the record sources store the picked record's id. */
const DATATYPES: Record<Exclude<OptionsSource, 'own' | 'users'>, string> = {
	clientContacts: 'Contact',
	companies: 'Account',
	products: 'Product'
};

export const sourceOfDatatype = (datatype: string): OptionsSource => {
	if (datatype === 'User' || datatype === 'Users') {
		return 'users';
	}
	const source = (Object.keys(DATATYPES) as (keyof typeof DATATYPES)[]).find(key => DATATYPES[key] === datatype);
	return source ?? 'own';
};

export const datatypeOfSource = (
	source: OptionsSource,
	{ multipleOptions, multipleUsers }: { multipleOptions?: boolean; multipleUsers?: boolean }
): string => {
	if (source === 'own') {
		return multipleOptions ? 'MultiSelect' : 'Select';
	}
	if (source === 'users') {
		return multipleUsers ? 'Users' : 'User';
	}
	return DATATYPES[source];
};

type Item = { id: OptionsSource; title: string };

type Props = {
	sources: OptionsSource[];
	value: OptionsSource;
	onChange: (source: OptionsSource) => void;
};

const OptionsFromSelect = ({ sources, value, onChange }: Props) => {
	const { t } = useTranslation();
	const items: Item[] = sources.map(id => ({ id, title: t(LABELS[id]) }));

	return (
		<Block space="mbm">
			<Label>{t('admin.fields.fieldDependency.optionsFrom')}</Label>
			<Select<Item>
				anchor=".Modals"
				options={items}
				value={items.find(item => item.id === value) ?? items[0]}
				onChange={item => onChange(item?.id ?? 'own')}
			/>
		</Block>
	);
};

export default OptionsFromSelect;
